import { useState, useEffect, useCallback } from 'react';
import {
  Users, Lightbulb, Heart, Trophy, Handshake, Flag,
} from 'lucide-react';

import api from '../../../services/api';
import { extractListData } from '../../../utils/Utils';

// Faz D (spec 2026-08-08 §4, Task 5, D5) — Takdir (Recognition) FE veri
// katmanı. Gerçek `RecognitionController` uçları (task-3-report.md §1):
//   GET  v1/recognitions/my    → { received, sent } (MyRecognitionsDto — private DAHİL)
//   GET  v1/recognitions/wall  ?pageNumber&pageSize → PaginatedList<RecognitionWallRowDto> (YALNIZ public)
//   POST v1/recognitions       { receiverEmployeeId, badgeKey, text?, isPublic }
//
// Rozet anahtarları RecognitionDataAccess.ValidBadgeKeys İLE BİREBİR (task-3-report.md
// §"Rozet anahtarları" — tek doğru kaynak BE'dir, burada YENİ bir anahtar
// İCAT EDİLMEDİ): teamwork | innovation | customer_focus | excellence |
// mentorship | ownership. İkon + gradyan burada eşlenir (match-design-language
// memory: menü/rozet ikonları GRADYAN — my-badges/MyBadgesPage.js'teki
// `linear-gradient(135deg, from, to)` dairesel ikon kutusu deseni birebir
// izlendi, training-leaderboard/TrainingLeaderboardPage.js'teki rütbe
// rozetleriyle AYNI görsel dil).
export const RECOGNITION_BADGES = [
  { key: 'teamwork', Icon: Users, from: '#0291EF', to: '#38BDF8' },
  { key: 'innovation', Icon: Lightbulb, from: '#8B5CF6', to: '#C4B5FD' },
  { key: 'customer_focus', Icon: Heart, from: '#EC4899', to: '#F9A8D4' },
  { key: 'excellence', Icon: Trophy, from: '#F59E0B', to: '#FBBF24' },
  { key: 'mentorship', Icon: Handshake, from: '#02D3BF', to: '#5EEAD4' },
  { key: 'ownership', Icon: Flag, from: '#4F46E5', to: '#818CF8' },
];

export const RECOGNITION_BADGE_MAP = Object.fromEntries(
  RECOGNITION_BADGES.map((b) => [b.key, b]),
);

// Çalışan seçici — continuous-feedback/config.buildEmployeeAsync'in BAĞIMSIZ
// kopyası (repo konvansiyonu: farklı sayfa grubu = bağımsız kopya).
export function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const items = extractListData(res);
      return items.map((e) => ({
        id: e.id,
        name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture } : null;
    },
  };
}

export const employeeAsync = buildEmployeeAsync();

// Kendini hariç tutan seçici — SendFeedbackPage/RequestFeedbackModal'daki
// buildSelfExcludedEmployeeAsync ile AYNI mantığın bu sayfa grubundaki
// bağımsız kopyası. Gerçek koruma her zaman backend'dedir
// (performance.recognition.selfRecognition — allIds kümesiyle, task-3-report.md §1).
export function buildSelfExcludedEmployeeAsync(excludeEmployeeId) {
  return {
    onSearch: async (q) => {
      const results = await employeeAsync.onSearch(q);
      if (!excludeEmployeeId) return results;
      return results.filter((r) => String(r.id) !== String(excludeEmployeeId));
    },
    onGetById: employeeAsync.onGetById,
  };
}

// GET v1/recognitions/my — ResponseObjectModel.data = { received, sent }
// (MyRecognitionsDto, task-3-report.md §1 — alan sırası received ÖNCE).
// Private (isPublic:false) DAHİL — taraflar zaten gönderen/alıcı.
export const useMyRecognitionsApi = () => {
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    api.get('/v1/recognitions/my')
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.status ? res.data.data : null;
        setReceived(data?.received || []);
        setSent(data?.sent || []);
      })
      .catch(() => { if (!cancelled) { setReceived([]); setSent([]); } })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  return {
    received, sent, fetching, refresh,
  };
};

export const WALL_PAGE_SIZE = 20;

// GET v1/recognitions/wall — YALNIZ isPublic:true (task-3-report.md §1),
// standart PaginatedList<RecognitionWallRowDto> (data/totalRowCount/pageIndex/
// pageSize). BE `pageSize`i 50'de kırpar — burada 20 istenir (varsayılan),
// üst sınırı zorlamaya gerek yok.
export async function getRecognitionWall(pageNumber = 1, pageSize = WALL_PAGE_SIZE) {
  return api.get('/v1/recognitions/wall', { params: { pageNumber, pageSize } });
}

export const useRecognitionWallApi = () => {
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    getRecognitionWall(pageNumber, WALL_PAGE_SIZE)
      .then((res) => {
        if (cancelled) return;
        const result = res.data?.status ? res.data.data : null;
        setItems(result?.data || []);
        setTotalRowCount(result?.totalRowCount || 0);
      })
      .catch(() => { if (!cancelled) { setItems([]); setTotalRowCount(0); } })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [pageNumber, refreshTrigger]);

  return {
    items,
    fetching,
    pageNumber,
    setPageNumber,
    totalRowCount,
    pageSize: WALL_PAGE_SIZE,
    hasPrev: pageNumber > 1,
    hasNext: pageNumber * WALL_PAGE_SIZE < totalRowCount,
    refresh,
  };
};

// POST v1/recognitions — body { receiverEmployeeId, badgeKey, text?, isPublic }.
export const useCreateRecognitionApi = () => {
  const [sending, setSending] = useState(false);

  const create = useCallback(async (payload) => {
    setSending(true);
    try {
      const res = await api.post('/v1/recognitions', payload);
      return res.data?.data || null;
    } catch {
      return null;
    } finally {
      setSending(false);
    }
  }, []);

  return { create, sending };
};
